import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Snackbar,
  Button,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import * as XLSX from "xlsx";
import {
  fetchAppUsers,
  fetchAppUsersPage,
  fetchAppUsersByFilter,
  fetchDynamicForms,
} from "../utils/api";

const PROFILE_FORM_KEYS = [
  "USER_REGISTRATION_DYNAMIC_FIELDS",
  "REGISTRATION_DYNAMIC_FIELDS",
  "USER_PROFILE_FIELDS",
  "REGISTRATION_FIELDS",
];
function getProfileFormFieldKeys(forms) {
  const list = Array.isArray(forms) ? forms : [];
  const normalized = (key) => (key || "").toUpperCase().replace(/\s+/g, "_");
  const form = list.find(
    (f) =>
      PROFILE_FORM_KEYS.includes(normalized(f.formKey)) ||
      normalized(f.formKey).includes("REGISTRATION") ||
      normalized(f.formKey).includes("USER_PROFILE")
  );
  if (!form || !Array.isArray(form.dynamicFields)) return [];
  return form.dynamicFields.map((f) => f.fieldKey).filter(Boolean);
}
function isSubmissionFieldFilled(user, fieldKey) {
  if (!user.submissionData || !Array.isArray(user.submissionData)) return false;
  const field = user.submissionData.find((item) => item.fieldKey === fieldKey);
  if (!field) return false;
  const raw = field.answerValue;
  if (raw === null || raw === undefined) return false;
  if (Array.isArray(raw)) {
    const values = raw.filter((v) => v != null && String(v).trim() !== "");
    return values.length > 0;
  }
  return String(raw).trim() !== "";
}
function getProfileFilledCount(user, expectedFieldKeys) {
  if (!Array.isArray(expectedFieldKeys) || expectedFieldKeys.length === 0) return 0;
  return expectedFieldKeys.filter((key) => isSubmissionFieldFilled(user, key)).length;
}
function isProfileComplete(user, expectedFieldKeys) {
  const count = getProfileFilledCount(user, expectedFieldKeys);
  return count === expectedFieldKeys.length && expectedFieldKeys.length > 0;
}

const AppUsersTable = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchFilters, setSearchFilters] = useState({
    firstName: "",
    lastName: "",
    mobile: ""
  });
  const [searchedUsers, setSearchedUsers] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [profileFormFieldKeys, setProfileFormFieldKeys] = useState([]);

  // Check if user is super admin
  const isSuperAdmin = useMemo(() => {
    const userGroupName = localStorage.getItem('userGroupName') || '';
    const normalizedGroupName = userGroupName.toLowerCase().replace(/[\s_-]+/g, '');
    return normalizedGroupName === 'superadmin' || normalizedGroupName === 'super-admin';
  }, []);

  const renderName = (user) =>
    [user.firstName, user.middleName, user.lastName]
      .filter(Boolean)
      .join(" ") || "-";

  useEffect(() => {
    let cancelled = false;
    const loadProfileForm = async () => {
      try {
        const formsResponse = await fetchDynamicForms().catch(() => ({ data: [] }));
        if (cancelled) return;
        setProfileFormFieldKeys(getProfileFormFieldKeys(formsResponse?.data));
      } catch (err) {
        console.error("Error fetching profile form:", err);
      }
    };
    loadProfileForm();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isSuperAdmin || hasSearched) return undefined;

    let cancelled = false;
    const loadPage = async () => {
      setListLoading(true);
      setError(null);
      try {
        const res = await fetchAppUsersPage({ pageNo: page, pageSize: rowsPerPage });
        if (cancelled) return;
        const payload = res?.data;
        const content = Array.isArray(payload?.content) ? payload.content : [];
        setUsers(content);
        setTotalCount(
          payload?.page?.totalElements ?? payload?.totalElements ?? content.length
        );
      } catch (err) {
        console.error("Error fetching app users:", err);
        if (!cancelled) {
          setError("Failed to load registered users. Please try again later.");
          setUsers([]);
          setTotalCount(0);
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    };

    loadPage();
    return () => {
      cancelled = true;
    };
  }, [isSuperAdmin, hasSearched, page, rowsPerPage]);

  // Handle search button click - use API endpoint for both admin and non-admin
  const handleSearch = async () => {
    // Check if at least one filter is provided
    const hasFilter = searchFilters.firstName.trim() || 
                     searchFilters.lastName.trim() || 
                     searchFilters.mobile.trim();

    if (!hasFilter) {
      setSearchedUsers([]);
      setHasSearched(false);
      setPage(0);
      return;
    }

    setHasSearched(true);
    setSearchLoading(true);
    setError(null);

    try {
      const response = await fetchAppUsersByFilter({
        firstName: searchFilters.firstName.trim() || undefined,
        lastName: searchFilters.lastName.trim() || undefined,
        mobile: searchFilters.mobile.trim() || undefined
      });

      console.log('Search API response:', response);
      console.log('Search filters used:', {
        firstName: searchFilters.firstName.trim() || undefined,
        lastName: searchFilters.lastName.trim() || undefined,
        mobile: searchFilters.mobile.trim() || undefined
      });

      // The API might return an array or a single object
      const result = response.data;
      console.log('Search result data:', result);
      
      if (Array.isArray(result)) {
        setSearchedUsers(result);
        setPage(0);
      } else if (result && typeof result === 'object') {
        setSearchedUsers([result]);
        setPage(0);
      } else {
        setSearchedUsers([]);
      }
    } catch (err) {
      console.error('Error searching users:', err);
      setSearchedUsers([]);
      // Don't show error if it's just "not found" - that's expected
      if (err.response?.status !== 404) {
        setError('Failed to search users. Please try again later.');
      }
    } finally {
      setSearchLoading(false);
    }
  };

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderAddress = (user) => {
    const parts = [
      user.address,
      user.town,
      user.taluka?.talukaName,
      user.district?.districtName,
      user.state?.stateName,
      user.pinCode,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(", ") : "-";
  };

  const displayUsers = hasSearched ? searchedUsers : users;
  const paginationCount = hasSearched ? searchedUsers.length : totalCount;
  const pagedDisplayUsers = hasSearched
    ? displayUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : displayUsers;

  // Extract value from submissionData by fieldKey (supports array answerValue for dropdowns)
  const getSubmissionValue = (user, fieldKey) => {
    if (!user.submissionData || !Array.isArray(user.submissionData)) {
      return "-";
    }

    const field = user.submissionData.find(item => item.fieldKey === fieldKey);
    if (!field) {
      return "-";
    }

    const raw = field.answerValue;
    const isArray = Array.isArray(raw);
    const values = isArray ? raw.filter(v => v != null && String(v).trim() !== '').map(String) : [];

    if (field.fieldType === 'dropdown') {
      if (isArray && values.length === 0) return "-";
      if (isArray && values.length > 0) {
        try {
          const optionsJson = field.optionsJson
            ? (typeof field.optionsJson === 'string' ? JSON.parse(field.optionsJson) : field.optionsJson)
            : null;
          if (optionsJson && typeof optionsJson === 'object') {
            if (Array.isArray(optionsJson)) {
              const labels = values.map(id =>
                optionsJson.find(o => String(o.value ?? o.id ?? o.professionId ?? o.educationId) === id)
              ).map(o => o?.label ?? o?.name ?? o?.professionName ?? o?.educationName).filter(Boolean);
              if (labels.length) return labels.join(", ");
            } else {
              const nameKey = Object.keys(optionsJson).find(k => k.includes('Name') || k.includes('name'));
              if (nameKey && values.length === 1) return optionsJson[nameKey];
            }
          }
        } catch (e) {
          console.error('Error parsing optionsJson:', e);
        }
        return values.join(", ");
      }
      if (!isArray && raw != null && raw !== undefined && String(raw).trim() !== '') {
        try {
          const optionsJson = field.optionsJson
            ? (typeof field.optionsJson === 'string' ? JSON.parse(field.optionsJson) : field.optionsJson)
            : null;
          if (optionsJson && typeof optionsJson === 'object' && !Array.isArray(optionsJson)) {
            const nameKey = Object.keys(optionsJson).find(k => k.includes('Name') || k.includes('name'));
            if (nameKey) return optionsJson[nameKey];
          }
        } catch (e) {
          console.error('Error parsing optionsJson:', e);
        }
        return String(raw);
      }
      return "-";
    }

    return raw !== null && raw !== undefined ? String(raw) : "-";
  };

  // Get field label from submissionData
  const getFieldLabel = (user, fieldKey) => {
    if (!user.submissionData || !Array.isArray(user.submissionData)) {
      return fieldKey;
    }
    const field = user.submissionData.find(item => item.fieldKey === fieldKey);
    return field?.fieldLabel || fieldKey;
  };

  const renderProfileStatus = (user) => {
    if (!profileFormFieldKeys || profileFormFieldKeys.length === 0) {
      return (
        <Tooltip title="Profile form not configured">
          <Typography component="span" variant="caption" color="text.secondary">—</Typography>
        </Tooltip>
      );
    }
    const complete = isProfileComplete(user, profileFormFieldKeys);
    const filled = getProfileFilledCount(user, profileFormFieldKeys);
    const total = profileFormFieldKeys.length;
    if (complete) {
      return (
        <Tooltip title={`Profile complete (${filled}/${total} fields)`}>
          <CheckCircleIcon color="success" fontSize="small" />
        </Tooltip>
      );
    }
    return (
      <Tooltip title={`Profile incomplete (${filled}/${total} fields)`}>
        <WarningAmberIcon color="warning" fontSize="small" />
      </Tooltip>
    );
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const hideSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };


  const getAllSubmissionFieldsFromList = (userList) => {
    const fieldKeys = new Set();
    userList.forEach((user) => {
      if (user.submissionData && Array.isArray(user.submissionData)) {
        user.submissionData.forEach((field) => {
          if (field.fieldKey) fieldKeys.add(field.fieldKey);
        });
      }
    });
    return Array.from(fieldKeys);
  };

  // Export users data to Excel (loads full list for export)
  const handleExportToExcel = async () => {
    try {
      const usersResponse = await fetchAppUsers();
      const exportUsers = Array.isArray(usersResponse?.data) ? usersResponse.data : [];
      if (exportUsers.length === 0) {
        showSnackbar("No data to export", "warning");
        return;
      }

      const submissionFields = getAllSubmissionFieldsFromList(exportUsers);

      const headers = [
        'ID',
        'Name',
        'Mobile',
        'Profession',
        'Address',
        ...submissionFields.map(fieldKey => {
          const userWithField = exportUsers.find(u =>
            u.submissionData?.some(f => f.fieldKey === fieldKey)
          );
          return userWithField ? getFieldLabel(userWithField, fieldKey) : fieldKey;
        })
      ];

      const dataRows = exportUsers.map(user => {
        const row = [
          user.userRegistrationId || '-',
          renderName(user),
          user.mobile || '-',
          user.profession?.professionName || getSubmissionValue(user, 'profession') || 'Not Specified',
          renderAddress(user),
          ...submissionFields.map(fieldKey => getSubmissionValue(user, fieldKey))
        ];
        return row;
      });

      // Create worksheet
      const worksheetData = [headers, ...dataRows];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Set column widths
      const columnWidths = headers.map((header, index) => {
        const maxLength = Math.max(
          header.length,
          ...dataRows.map(row => String(row[index] || '').length)
        );
        return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
      });
      worksheet['!cols'] = columnWidths;

      // Create workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');

      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `users_export_${timestamp}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);
      showSnackbar("Users data exported successfully!", "success");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      showSnackbar("Failed to export data. Please try again.", "error");
    }
  };

  return (
    <Box sx={{ width: "95%", mx: "auto", mt: 5 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" align="center" sx={{ flex: 1 }}>
          Registered Users
        </Typography>
        {isSuperAdmin && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportToExcel}
            disabled={listLoading}
            sx={{ ml: 2 }}
          >
            Export to Excel
          </Button>
        )}
      </Box>

      {/* Search Box - shown for both admin and non-admin */}
      {!isSuperAdmin ? (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            <TextField
              label="First Name"
              placeholder="Enter first name"
              value={searchFilters.firstName}
              onChange={(e) => {
                setSearchFilters({ ...searchFilters, firstName: e.target.value });
                setHasSearched(false);
                setSearchedUsers([]);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="Last Name"
              placeholder="Enter last name"
              value={searchFilters.lastName}
              onChange={(e) => {
                setSearchFilters({ ...searchFilters, lastName: e.target.value });
                setHasSearched(false);
                setSearchedUsers([]);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="Mobile Number"
              placeholder="Enter mobile number"
              value={searchFilters.mobile}
              onChange={(e) => {
                setSearchFilters({ ...searchFilters, mobile: e.target.value });
                setHasSearched(false);
                setSearchedUsers([]);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              sx={{ minWidth: 200 }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={searchLoading}
            >
              Search
            </Button>
          </Box>
        </Box>
      ) : (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
            <TextField
              label="First Name"
              placeholder="Enter first name"
              value={searchFilters.firstName}
              onChange={(e) => {
                setSearchFilters({ ...searchFilters, firstName: e.target.value });
                setHasSearched(false);
                setPage(0);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="Last Name"
              placeholder="Enter last name"
              value={searchFilters.lastName}
              onChange={(e) => {
                setSearchFilters({ ...searchFilters, lastName: e.target.value });
                setHasSearched(false);
                setPage(0);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              sx={{ minWidth: 200 }}
            />
            <TextField
              label="Mobile Number"
              placeholder="Enter mobile number"
              value={searchFilters.mobile}
              onChange={(e) => {
                setSearchFilters({ ...searchFilters, mobile: e.target.value });
                setHasSearched(false);
                setPage(0);
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              sx={{ minWidth: 200 }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={searchLoading}
            >
              Search
            </Button>
          </Box>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {isSuperAdmin && (
        <Paper sx={{ mt: 1 }}>
          {listLoading || searchLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: '80vh', overflowX: 'auto' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Mobile Number</TableCell>
                      <TableCell align="center">Profile</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedDisplayUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          {hasSearched ? "No users found matching your search." : "No registered users found."}
                        </TableCell>
                      </TableRow>
                    ) : (
                      pagedDisplayUsers.map((user) => (
                        <TableRow key={user.userRegistrationId} hover>
                          <TableCell>{renderName(user)}</TableCell>
                          <TableCell>{user.mobile || "-"}</TableCell>
                          <TableCell align="center">{renderProfileStatus(user)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={paginationCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </Paper>
      )}

      {!isSuperAdmin && hasSearched && (
        <Box sx={{ mt: 3 }}>
          {searchLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : searchedUsers.length > 0 ? (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Mobile Number</TableCell>
                    <TableCell align="center">Profile</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchedUsers.map((user) => (
                    <TableRow key={user.userRegistrationId}>
                      <TableCell>{renderName(user)}</TableCell>
                      <TableCell>{user.mobile || "-"}</TableCell>
                      <TableCell align="center">{renderProfileStatus(user)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Paper sx={{ p: 3, textAlign: 'center', maxWidth: 500, mx: 'auto' }}>
              <Typography variant="body1" color="textSecondary">
                No user found
              </Typography>
            </Paper>
          )}
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={hideSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AppUsersTable;
